import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogFormGrid, DialogFormSection,
} from './ui/dialog';
import { Badge } from './ui/badge';
import { eduResearchAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { hasAnyRole } from '../lib/rbac';
import { BookOpen, Plus, Pencil, Trash2, Loader2, FileText, Award, Quote, DollarSign, IdCard } from 'lucide-react';
import Swal from 'sweetalert2';
import { EmptyState } from './common/States';
import { StatCard } from './common/StatCard';

const OUTPUT_TYPES = ['publication', 'patent', 'grant', 'book', 'chapter', 'consultancy'];
const TYPE_LABEL = {
  publication: 'Publication', patent: 'Patent', grant: 'Grant',
  book: 'Book', chapter: 'Chapter', consultancy: 'Consultancy',
};

const CURRENT_YEAR = new Date().getFullYear();

const EMPTY_FORM = {
  faculty_profile_id: '', output_type: 'publication', title: '', venue: '',
  year: CURRENT_YEAR, doi: '', citations: 0, amount: '', co_authors: '',
};

const EMPTY_IDS = { faculty_profile_id: '', orcid: '', scopus_id: '', google_scholar: '' };

const toArray = (res) => {
  const d = res?.data;
  if (Array.isArray(d)) return d;
  return d?.items || d?.results || [];
};

const EducationResearchManagement = () => {
  const { user } = useAuth();
  const canManage = Boolean(user?.isSuperadmin) || hasAnyRole(user?.role, ['admin', 'workspace_admin', 'hr_admin']);

  const [loading, setLoading] = useState(false);
  const [outputs, setOutputs] = useState([]);
  const [report, setReport] = useState(null);

  // filters
  const [filterFaculty, setFilterFaculty] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterYear, setFilterYear] = useState('');
  const [reportYear, setReportYear] = useState(CURRENT_YEAR);

  // output dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  // researcher-ids dialog
  const [idsOpen, setIdsOpen] = useState(false);
  const [idsForm, setIdsForm] = useState(EMPTY_IDS);

  const loadOutputs = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterFaculty) params.faculty_profile_id = Number(filterFaculty);
      if (filterType && filterType !== 'all') params.output_type = filterType;
      if (filterYear) params.year = Number(filterYear);
      const res = await eduResearchAPI.listOutputs(params);
      setOutputs(toArray(res));
    } catch (err) {
      if (err?.response?.status === 402) {
        setOutputs([]);
      } else {
        Swal.fire('Could not load research outputs', err?.response?.data?.detail || 'Please try again.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadReport = async () => {
    try {
      const res = await eduResearchAPI.annualReport(reportYear ? { year: Number(reportYear) } : {});
      setReport(res.data);
    } catch (err) {
      if (err?.response?.status !== 402) {
        // Non-fatal: report is a summary widget.
      }
      setReport(null);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- re-run on filter change; loadOutputs is stable
  useEffect(() => { loadOutputs(); }, [filterFaculty, filterType, filterYear]);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- re-run on year change; loadReport is stable
  useEffect(() => { loadReport(); }, [reportYear]);

  const stats = useMemo(() => {
    const totals = report?.totals || {};
    const byType = report?.by_type || {};
    return {
      outputs: totals.outputs || 0,
      citations: totals.citations || 0,
      grantAmount: totals.grant_amount || 0,
      publications: byType.publication || 0,
    };
  }, [report]);

  const openCreate = () => { setEditingId(null); setForm(EMPTY_FORM); setDialogOpen(true); };
  const openEdit = (row) => {
    setEditingId(row.id);
    setForm({
      ...EMPTY_FORM,
      ...row,
      faculty_profile_id: row.faculty_profile_id != null ? String(row.faculty_profile_id) : '',
      amount: row.amount != null ? row.amount : '',
      co_authors: Array.isArray(row.co_authors) ? row.co_authors.join(', ') : '',
    });
    setDialogOpen(true);
  };

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const setIdsField = (key, value) => setIdsForm((prev) => ({ ...prev, [key]: value }));

  const submit = async () => {
    if (!form.title?.trim()) return Swal.fire('Title required', 'Enter the title of the research output.', 'warning');
    const payload = {
      output_type: form.output_type,
      title: form.title.trim(),
      venue: form.venue || null,
      year: form.year ? Number(form.year) : null,
      doi: form.doi || null,
      citations: Number(form.citations) || 0,
      amount: form.amount !== '' && form.amount != null ? Number(form.amount) : null,
      faculty_profile_id: form.faculty_profile_id !== '' ? Number(form.faculty_profile_id) : null,
      co_authors: form.co_authors
        ? String(form.co_authors).split(',').map((s) => s.trim()).filter(Boolean)
        : [],
    };
    try {
      if (editingId) {
        await eduResearchAPI.updateOutput(editingId, payload);
      } else {
        await eduResearchAPI.createOutput(payload);
      }
      setDialogOpen(false);
      await loadOutputs();
      await loadReport();
      Swal.fire({ icon: 'success', title: editingId ? 'Output updated' : 'Output added', timer: 1400, showConfirmButton: false });
    } catch (err) {
      Swal.fire('Save failed', err?.response?.data?.detail || 'Please review the form and try again.', 'error');
    }
  };

  const remove = async (row) => {
    const ok = await Swal.fire({
      icon: 'warning', title: 'Delete research output?',
      text: `"${row.title}" will be permanently removed.`,
      showCancelButton: true, confirmButtonText: 'Delete', confirmButtonColor: '#b5456b',
    });
    if (!ok.isConfirmed) return;
    try {
      await eduResearchAPI.deleteOutput(row.id);
      await loadOutputs();
      await loadReport();
      Swal.fire({ icon: 'success', title: 'Deleted', timer: 1200, showConfirmButton: false });
    } catch (err) {
      Swal.fire('Delete failed', err?.response?.data?.detail || 'Please try again.', 'error');
    }
  };

  const openIds = async () => { setIdsForm(EMPTY_IDS); setIdsOpen(true); };

  const loadIds = async (facultyProfileId) => {
    if (!facultyProfileId) return;
    try {
      const res = await eduResearchAPI.listResearcherIds({ faculty_profile_id: Number(facultyProfileId) });
      const rows = toArray(res);
      if (rows.length) {
        const r = rows[0];
        setIdsForm({
          faculty_profile_id: String(r.faculty_profile_id),
          orcid: r.orcid || '', scopus_id: r.scopus_id || '', google_scholar: r.google_scholar || '',
        });
      }
    } catch (err) {
      /* non-fatal */
    }
  };

  const submitIds = async () => {
    if (!idsForm.faculty_profile_id) return Swal.fire('Faculty required', 'Enter the faculty profile ID.', 'warning');
    try {
      await eduResearchAPI.upsertResearcherIds({
        faculty_profile_id: Number(idsForm.faculty_profile_id),
        orcid: idsForm.orcid || null,
        scopus_id: idsForm.scopus_id || null,
        google_scholar: idsForm.google_scholar || null,
      });
      setIdsOpen(false);
      Swal.fire({ icon: 'success', title: 'Researcher IDs saved', timer: 1400, showConfirmButton: false });
    } catch (err) {
      Swal.fire('Save failed', err?.response?.data?.detail || 'Please try again.', 'error');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold flex items-center gap-2">
            <BookOpen className="h-6 w-6 text-indigo-600" /> Research & Publications
          </h2>
          <p className="text-sm text-muted-foreground">
            Scholarly outputs, external researcher identifiers and an annual research report.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {canManage && (
            <Button variant="outline" onClick={openIds}>
              <IdCard className="h-4 w-4 mr-1" /> Researcher IDs
            </Button>
          )}
          {canManage && (
            <Button onClick={openCreate}>
              <Plus className="h-4 w-4 mr-1" /> Add Output
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Total Outputs" value={stats.outputs} icon={FileText} />
        <StatCard label="Publications" value={stats.publications} icon={BookOpen} />
        <StatCard label="Total Citations" value={stats.citations} icon={Quote} />
        <StatCard label="Grant / Consultancy Value" value={stats.grantAmount} icon={DollarSign} />
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle>Research Outputs</CardTitle>
              <CardDescription>Publications, patents, grants, books, chapters and consultancy.</CardDescription>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Input
                className="w-36"
                placeholder="Faculty profile ID"
                value={filterFaculty}
                onChange={(e) => setFilterFaculty(e.target.value)}
              />
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-40"><SelectValue placeholder="All types" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  {OUTPUT_TYPES.map((t) => <SelectItem key={t} value={t}>{TYPE_LABEL[t]}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input
                className="w-28"
                type="number"
                placeholder="Year"
                value={filterYear}
                onChange={(e) => setFilterYear(e.target.value)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading research outputs…
            </div>
          ) : outputs.length === 0 ? (
            <EmptyState
              icon={BookOpen}
              title="No research outputs yet"
              description={canManage ? 'Add a publication, patent or grant to get started.' : 'No research outputs have been recorded yet.'}
            />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Venue</TableHead>
                    <TableHead>Year</TableHead>
                    <TableHead>Citations</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {outputs.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="font-medium">{row.title}</TableCell>
                      <TableCell><Badge variant="secondary">{TYPE_LABEL[row.output_type] || row.output_type}</Badge></TableCell>
                      <TableCell>{row.venue || '—'}</TableCell>
                      <TableCell>{row.year || '—'}</TableCell>
                      <TableCell>{row.citations || 0}</TableCell>
                      <TableCell>{row.amount != null ? row.amount : '—'}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        {canManage && <Button variant="ghost" size="icon" onClick={() => openEdit(row)} title="Edit"><Pencil className="h-4 w-4" /></Button>}
                        {canManage && <Button variant="ghost" size="icon" onClick={() => remove(row)} title="Delete"><Trash2 className="h-4 w-4 text-rose-600" /></Button>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2"><Award className="h-5 w-5 text-indigo-600" /> Annual Research Report</CardTitle>
              <CardDescription>Aggregated output counts, citations and grant value{report?.year ? ` for ${report.year}` : ''}.</CardDescription>
            </div>
            <Input
              className="w-28"
              type="number"
              placeholder="Year"
              value={reportYear}
              onChange={(e) => setReportYear(e.target.value)}
            />
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {OUTPUT_TYPES.map((t) => (
              <div key={t} className="flex items-center justify-between rounded-md border px-3 py-2 text-sm">
                <span className="text-muted-foreground">{TYPE_LABEL[t]}</span>
                <span className="font-semibold">{report?.by_type?.[t] || 0}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Create / edit output dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Research Output' : 'Add Research Output'}</DialogTitle>
          </DialogHeader>
          <DialogFormSection>
            <DialogFormGrid>
              <div className="space-y-1">
                <Label>Output Type</Label>
                <Select value={form.output_type} onValueChange={(v) => setField('output_type', v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {OUTPUT_TYPES.map((t) => <SelectItem key={t} value={t}>{TYPE_LABEL[t]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label>Faculty Profile ID</Label>
                <Input value={form.faculty_profile_id} onChange={(e) => setField('faculty_profile_id', e.target.value)} placeholder="e.g. 12" />
              </div>
              <div className="space-y-1">
                <Label>Venue</Label>
                <Input value={form.venue || ''} onChange={(e) => setField('venue', e.target.value)} placeholder="Journal / Conference" />
              </div>
              <div className="space-y-1">
                <Label>Year</Label>
                <Input type="number" value={form.year || ''} onChange={(e) => setField('year', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label>DOI</Label>
                <Input value={form.doi || ''} onChange={(e) => setField('doi', e.target.value)} placeholder="10.xxxx/xxxxx" />
              </div>
              <div className="space-y-1">
                <Label>Citations</Label>
                <Input type="number" value={form.citations} onChange={(e) => setField('citations', e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label>Amount (grant / consultancy)</Label>
                <Input type="number" value={form.amount} onChange={(e) => setField('amount', e.target.value)} />
              </div>
            </DialogFormGrid>
            <div className="space-y-1">
              <Label>Title *</Label>
              <Input value={form.title} onChange={(e) => setField('title', e.target.value)} placeholder="Title of the publication / patent / grant" />
            </div>
            <div className="space-y-1">
              <Label>Co-authors (comma separated)</Label>
              <Input value={form.co_authors} onChange={(e) => setField('co_authors', e.target.value)} placeholder="Jane Doe, John Roe" />
            </div>
          </DialogFormSection>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={submit}>{editingId ? 'Save changes' : 'Add output'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Researcher IDs editor */}
      <Dialog open={idsOpen} onOpenChange={setIdsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Researcher IDs</DialogTitle>
          </DialogHeader>
          <DialogFormSection>
            <div className="space-y-1">
              <Label>Faculty Profile ID *</Label>
              <Input
                value={idsForm.faculty_profile_id}
                onChange={(e) => setIdsField('faculty_profile_id', e.target.value)}
                onBlur={(e) => loadIds(e.target.value)}
                placeholder="e.g. 12"
              />
            </div>
            <div className="space-y-1">
              <Label>ORCID</Label>
              <Input value={idsForm.orcid} onChange={(e) => setIdsField('orcid', e.target.value)} placeholder="0000-0000-0000-0000" />
            </div>
            <div className="space-y-1">
              <Label>Scopus ID</Label>
              <Input value={idsForm.scopus_id} onChange={(e) => setIdsField('scopus_id', e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>Google Scholar</Label>
              <Input value={idsForm.google_scholar} onChange={(e) => setIdsField('google_scholar', e.target.value)} />
            </div>
          </DialogFormSection>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIdsOpen(false)}>Cancel</Button>
            <Button onClick={submitIds}>Save IDs</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EducationResearchManagement;
